// PathBuilder.js — builds a connected, 4-directional (no-diagonal) path of
// distinct grid cells. The active line's kanji bars are placed along it, so
// walking bar-to-bar costs ~(count-1) orthogonal steps. Pure, no DOM.

/**
 * @param {number} count  number of cells needed (one per kanji bar)
 * @param {{cols:number, rows:number}} grid
 * @returns {Array<{col:number,row:number}>}  ordered, orthogonally adjacent cells
 */
export function buildPath(count, grid) {
    const { cols, rows } = grid;
    const n = Math.max(1, Math.min(count, cols * rows));

    // A few randomized self-avoiding walks; fall back to a guaranteed snake.
    for (let attempt = 0; attempt < 40; attempt++) {
        const path = walk(n, cols, rows);
        if (path) return path;
    }
    return snake(n, cols, rows);
}

// DFS self-avoiding walk with backtracking from a random start.
function walk(n, cols, rows) {
    const start = { col: rand(cols), row: rand(rows) };
    const path = [start];
    const used = new Set([key(start)]);
    let guard = n * cols * rows * 4; // cap work; bail to snake if exceeded

    while (path.length < n) {
        if (guard-- <= 0) return null;

        const head = path[path.length - 1];
        const open = shuffle(
            neighbors(head, cols, rows).filter((c) => !used.has(key(c)))
        );

        if (open.length === 0) {
            const dead = path.pop();
            used.delete(key(dead));
            if (path.length === 0) return null;
            continue;
        }
        const next = open[0];
        path.push(next);
        used.add(key(next));
    }
    return path;
}

// Boustrophedon (snake) — always orthogonal, always succeeds for n <= cols*rows.
function snake(n, cols, rows) {
    const path = [];
    for (let row = 0; row < rows; row++) {
        const xs = [];
        for (let col = 0; col < cols; col++) xs.push(col);
        if (row % 2 === 1) xs.reverse();
        for (const col of xs) {
            path.push({ col, row });
            if (path.length === n) return path;
        }
    }
    return path;
}

function neighbors({ col, row }, cols, rows) {
    const out = [];
    if (row > 0) out.push({ col, row: row - 1 });
    if (row < rows - 1) out.push({ col, row: row + 1 });
    if (col > 0) out.push({ col: col - 1, row });
    if (col < cols - 1) out.push({ col: col + 1, row });
    return out;
}

const key = (c) => `${c.col},${c.row}`;
const rand = (max) => Math.floor(Math.random() * max);

function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}
